import BlockContent from '@sanity/block-content-to-react'
import cx from 'classnames'

import {
  type SanityTechStackItem,
  type SanityTechStackModule,
} from '@data/sanity/queries/types/modules'
import { serializers } from '@lib/serializers'

import Photo from '@components/photo'

type TechStackProps = Pick<
  SanityTechStackModule,
  'heading' | 'content' | 'rows'
>

interface TechStackItemProps {
  item: SanityTechStackItem
  className?: string
}

const TechStackItem = ({ item, className }: TechStackItemProps) => {
  switch (item._type) {
    case 'figure': {
      return <Photo image={item} className={className} />
    }

    default: {
      return null
    }
  }
}

const TechStack = ({ heading, content, rows }: TechStackProps) => {
  return (
    <section className="relative container">
      <div className="py-10 sm:py-12 lg:py-16 border-t border-black border-opacity-25">
        <div className="grid grid-cols-12 gap-x-4 sm:gap-x-8 lg:gap-x-12 gap-y-8">
          <div className="col-span-12 sm:col-span-7">
            {!!heading && <h2>{heading}</h2>}
          </div>

          <div className="col-span-12 sm:col-span-5">
            {!!content && (
              <BlockContent
                blocks={content}
                serializers={serializers}
                renderContainerOnSingleChild
                className="rc"
              />
            )}
          </div>
        </div>

        {!!rows && (
          <div className="mt-12">
            {rows.map(({ _key, heading, subheading, items }, index) => (
              <div
                key={_key}
                className={cx(
                  'grid grid-cols-12 gap-x-4 sm:gap-x-8 lg:gap-x-12 gap-y-8 py-12',
                  {
                    'border-t border-black border-opacity-25': index > 0,
                  }
                )}
              >
                <div className="col-span-12 sm:col-span-7">
                  {!!heading && <h4>{heading}</h4>}
                  {!!subheading && (
                    <p className="mt-1 font-light text-gray-dark">
                      {subheading}
                    </p>
                  )}
                </div>

                <div className="col-span-12 sm:col-span-5 flex flex-wrap items-center gap-x-14 gap-y-8">
                  {items?.map((item) => (
                    <TechStackItem
                      key={item._key}
                      item={item}
                      className="flex-none"
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}

export default TechStack
